import React, { useState, useEffect } from 'react';
import {
    Typography,
    TextField,
    Button,
    IconButton,
    Checkbox,
    Chip,
    Grid,
    Box,
    Divider,
    Card,
    CardContent,
    CardActions,
    Tooltip,
    FormControlLabel,
    FormGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ListAlt as ListAltIcon,
    Save as SaveIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import GroupNameEditor from "@/components/adtag/GroupNameEditor";
import DomainPopup from "@/components/adtag/DomainPopup";
import DomainImportDialog from "@/components/adtag/DomainImportDialog";
import DomainManagerDialog from "@/components/adtag/DomainManagerDialog";

import api from "@/api/index.ts";
import {storeAdTagDomains} from "@/store/adTagDomains";
import {notification} from "antd";
import {getResponseErrorMessage} from "@/utils/api-service";

// Компонент группы
const SettingsGroup = ({ group, onDelete, onUpdate, onAddDomain, onRemoveDomain }) => {
    const [localGroup, setLocalGroup] = useState(group);
    const [domainPopupOpen, setDomainPopupOpen] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [managerDialogOpen, setManagerDialogOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isActive, setIsActive] = useState(true);

    // Полный список доменов в браузер не грузится: их может быть 100 000.
    // Карточка показывает агрегаты и превью первых строк.
    const previewDomains = localGroup.domains_preview ?? localGroup.domains ?? [];
    const remainingCount = Math.max(0, (localGroup.domains_count ?? 0) - previewDomains.length);

    // Состояние для диалога подтверждения удаления домена
    const [deleteDomainDialog, setDeleteDomainDialog] = useState({
        open: false,
        domainId: null,
        domainName: ''
    });

    // Состояние для диалога подтверждения удаления группы
    const [deleteGroupDialog, setDeleteGroupDialog] = useState({
        open: false,
        groupName: ''
    });

    useEffect(() => {
        setLocalGroup(group);
        setIsActive(group.is_active);
    }, [group]);

    const handleFieldChange = (field) => (event) => {
        const updated = {
            ...localGroup,
            [field]: event.target.value
        };
        setLocalGroup(updated);
    };

    const handleNameSave = async (newName) => {
        try {
            await api.adtag.updateGroup(group.id, {
                name: newName,
                preroll: localGroup.preroll,
                midroll: localGroup.midroll,
                postroll: localGroup.postroll,
                is_active: isActive
            });
            const updatedGroup = { ...localGroup, name: newName };
            setLocalGroup(updatedGroup);
            onUpdate(group.id, updatedGroup);
            setIsEditingName(false);
            notification.success({
                message: 'Название группы обновлено!'
            });
        } catch (error) {
            console.error('Ошибка обновления названия:', error);
            const messageFormatter = getResponseErrorMessage(error, 'Ошибка при обновлении названия группы')
            notification.error({
                message: messageFormatter
            });
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await api.adtag.updateGroup(group.id, {
                name: localGroup.name,
                preroll: localGroup.preroll,
                midroll: localGroup.midroll,
                postroll: localGroup.postroll,
                is_active: isActive
            });
            onUpdate(group.id, localGroup);
            notification.success({
                message: 'Группа обновлена!'
            });
        } catch (error) {
            console.error('Ошибка обновления:', error);
            const messageFormatter = getResponseErrorMessage(error, 'Ошибка при обновлении группы')
            notification.error({
                message: messageFormatter
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Открытие диалога подтверждения удаления группы
    const openDeleteGroupDialog = () => {
        setDeleteGroupDialog({
            open: true,
            groupName: group.name || 'Без названия'
        });
    };

    // Закрытие диалога удаления группы
    const closeDeleteGroupDialog = () => {
        setDeleteGroupDialog({
            open: false,
            groupName: ''
        });
    };

    // Подтверждение удаления группы
    const confirmDeleteGroup = async () => {
        try {
            await api.adtag.deleteGroup(group.id);
            onDelete(group.id);
            notification.success({
                message: 'Группа успешно удалена!'
            });
        } catch (error) {
            console.error('Ошибка удаления:', error);
            const messageFormatter = getResponseErrorMessage(error, 'Ошибка при удалении группы')
            notification.error({
                message: messageFormatter
            });
        } finally {
            closeDeleteGroupDialog();
        }
    };

    const handleAddDomain = async (domainName) => {
        try {
            // is_active обязателен в CreateTagDomainRequest: без него запрос
            // падал на валидации (422), поэтому домен из UI не добавлялся
            await api.adtag.createDomain(group.id, {
                domain: domainName,
                is_active: true
            });
            onAddDomain(group.id, domainName);
        } catch (error) {
            console.error('Ошибка добавления домена:', error);
            const messageFormatter = getResponseErrorMessage(error, 'Ошибка при добавлении домена')
            notification.error({
                message: messageFormatter
            });
        }
    };

    // Открытие диалога подтверждения удаления домена
    const openDeleteDomainDialog = (domainId, domainName) => {
        setDeleteDomainDialog({
            open: true,
            domainId,
            domainName
        });
    };

    // Закрытие диалога
    const closeDeleteDomainDialog = () => {
        setDeleteDomainDialog({
            open: false,
            domainId: null,
            domainName: ''
        });
    };

    // Подтверждение удаления домена
    const confirmRemoveDomain = async () => {
        const { domainId } = deleteDomainDialog;

        try {
            await api.adtag.deleteDomain(group.id, domainId);
            onRemoveDomain(group.id, domainId);
            notification.success({
                message: 'Домен успешно удалён!'
            });
        } catch (error) {
            console.error('Ошибка удаления домена:', error);
            const messageFormatter = getResponseErrorMessage(error, 'Ошибка при удалении домена')
            notification.error({
                message: messageFormatter
            });
        } finally {
            closeDeleteDomainDialog();
        }
    };

    return (
        <Card sx={{ mb: 2, width: 1 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    {isEditingName ? (
                        <GroupNameEditor
                            groupName={localGroup.name || ''}
                            onSave={handleNameSave}
                            onCancel={() => setIsEditingName(false)}
                        />
                    ) : (
                        <>
                            <Typography variant="h6">
                                {localGroup.name || 'Группа без названия'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Редактировать название">
                                    <IconButton
                                        onClick={() => setIsEditingName(true)}
                                        color="primary"
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Удалить группу">
                                    <IconButton onClick={openDeleteGroupDialog} color="error" size="small">
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item size={{ lg: 4 }}>
                        <TextField
                            fullWidth
                            label="Preroll"
                            value={localGroup.preroll || ''}
                            onChange={handleFieldChange('preroll')}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item size={{ lg: 4 }}>
                        <TextField
                            fullWidth
                            label="Midroll"
                            value={localGroup.midroll || ''}
                            onChange={handleFieldChange('midroll')}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item size={{ lg: 4 }}>
                        <TextField
                            fullWidth
                            label="Postroll"
                            value={localGroup.postroll || ''}
                            onChange={handleFieldChange('postroll')}
                            variant="outlined"
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                    />
                                }
                                label="Группа активна"
                            />
                        </FormGroup>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="subtitle1">Домены</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {(localGroup.domains_count ?? 0).toLocaleString('ru-RU')}
                            {' · активных '}
                            {(localGroup.active_domains_count ?? 0).toLocaleString('ru-RU')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setDomainPopupOpen(true)}
                            size="small"
                        >
                            Добавить домен
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ListAltIcon />}
                            onClick={() => {
                                storeAdTagDomains.setGroup(group.id);
                                setImportDialogOpen(true);
                            }}
                            size="small"
                        >
                            Добавить домены
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<SettingsIcon />}
                            onClick={() => {
                                setManagerDialogOpen(true);
                                void storeAdTagDomains.open(group.id);
                            }}
                            size="small"
                            disabled={(localGroup.domains_count ?? 0) === 0}
                        >
                            Управлять доменами
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, minHeight: '32px' }}>
                    {previewDomains.map((domain) => (
                        <Chip
                            key={domain.id}
                            label={domain.domain_display || domain.domain}
                            onDelete={() => openDeleteDomainDialog(domain.id, domain.domain_display || domain.domain)}
                            color="primary"
                            variant="outlined"
                            size="small"
                        />
                    ))}
                    {remainingCount > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            …и ещё {remainingCount.toLocaleString('ru-RU')}
                        </Typography>
                    )}
                    {(localGroup.domains_count ?? 0) === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Нет добавленных доменов
                        </Typography>
                    )}
                </Box>
            </CardContent>
            <CardActions>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!localGroup.preroll || !localGroup.midroll || !localGroup.postroll || isSaving}
                    fullWidth
                    startIcon={isSaving ? null : <SaveIcon />}
                >
                    {isSaving ? 'Сохранение...' : 'Сохранить группу'}
                </Button>
            </CardActions>

            <DomainPopup
                open={domainPopupOpen}
                onClose={() => setDomainPopupOpen(false)}
                onAdd={handleAddDomain}
            />

            <DomainImportDialog
                open={importDialogOpen}
                onClose={() => {
                    setImportDialogOpen(false);
                    onAddDomain(group.id, '');
                }}
            />

            <DomainManagerDialog
                open={managerDialogOpen}
                groupName={group.name}
                onClose={() => {
                    setManagerDialogOpen(false);
                    onRemoveDomain(group.id, null);
                }}
            />

            {/* Диалог подтверждения удаления группы */}
            <Dialog
                open={deleteGroupDialog.open}
                onClose={closeDeleteGroupDialog}
                aria-labelledby="alert-dialog-title-group"
                aria-describedby="alert-dialog-description-group"
            >
                <DialogTitle id="alert-dialog-title-group">
                    Удаление группы
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description-group">
                        Вы уверены, что хотите удалить группу <strong>"{deleteGroupDialog.groupName}"</strong>?
                        <br />
                        Это действие нельзя отменить. Все связанные домены также будут удалены.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteGroupDialog} color="primary">
                        Отмена
                    </Button>
                    <Button onClick={confirmDeleteGroup} color="error" autoFocus>
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог подтверждения удаления домена */}
            <Dialog
                open={deleteDomainDialog.open}
                onClose={closeDeleteDomainDialog}
                aria-labelledby="alert-dialog-title-domain"
                aria-describedby="alert-dialog-description-domain"
            >
                <DialogTitle id="alert-dialog-title-domain">
                    Удаление домена
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description-domain">
                        Вы уверены, что хотите удалить домен <strong>"{deleteDomainDialog.domainName}"</strong>?
                        <br />
                        Это действие нельзя отменить.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDomainDialog} color="primary">
                        Отмена
                    </Button>
                    <Button onClick={confirmRemoveDomain} color="error" autoFocus>
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

export default SettingsGroup;